import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Stagger, StaggerItem, LineReveal } from "@/components/Reveal";
import { BUSINESS, AREAS } from "@/data/site";

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact Julisa's House Cleaning | Kansas City Cleaning Company"
        description="Contact Julisa's House Cleaning for residential and commercial cleaning across the Kansas City metro. Call 913-244-6114 or email julisasprofessionalcleaning@gmail.com."
      />

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-200/60 via-sky-100/50 to-white pt-14 pb-16 sm:pt-20 sm:pb-24">
        <div className="pointer-events-none absolute -top-24 -left-24 h-80 w-80 rounded-full bg-sky-100/70 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
              Contact
            </p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              <LineReveal lines={["We'd love to", <>hear from <span className="text-sky-600 italic">you</span></>]} />
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              Questions about a service, scheduling, or your space? Reach out directly — call,
              email, or find us on Facebook. For the fastest quote, use our
              {" "}<Link to="/quote" className="font-semibold text-sky-600 hover:underline">free quote form</Link>.
            </p>
          </div>

          <Stagger className="mt-12 grid gap-5 md:grid-cols-3">
            <StaggerItem>
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-phone-card"
                className="group flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-600 text-white shadow-md shadow-sky-600/25 transition-transform duration-300 group-hover:scale-110">
                  <Phone className="h-6 w-6" aria-hidden="true" />
                </span>
                <h2 className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Phone</h2>
                <p className="mt-2 text-2xl font-bold text-slate-900 group-hover:text-sky-600 transition-colors">{BUSINESS.phone}</p>
                <p className="mt-2 text-sm text-slate-600">Tap to call — we're happy to talk through your cleaning needs.</p>
              </a>
            </StaggerItem>

            <StaggerItem>
              <a
                href={BUSINESS.emailHref}
                data-testid="contact-email-card"
                className="group flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-600 text-white shadow-md shadow-sky-600/25 transition-transform duration-300 group-hover:scale-110">
                  <Mail className="h-6 w-6" aria-hidden="true" />
                </span>
                <h2 className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Email</h2>
                <p className="mt-2 break-all text-base font-bold text-slate-900 group-hover:text-sky-600 transition-colors">{BUSINESS.email}</p>
                <p className="mt-2 text-sm text-slate-600">Send us the details of your space and we'll reply with next steps.</p>
              </a>
            </StaggerItem>

            <StaggerItem>
              <a
                href={BUSINESS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook-button"
                className="group flex h-full flex-col rounded-2xl bg-sky-600 p-8 text-white shadow-lg shadow-sky-600/25 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur transition-transform duration-300 group-hover:scale-110">
                  <Facebook className="h-6 w-6" aria-hidden="true" />
                </span>
                <h2 className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-sky-200">Facebook</h2>
                <p className="mt-2 text-2xl font-bold">Follow Us on Facebook</p>
                <p className="mt-2 text-sm text-sky-100">Updates, photos of recent work, and an easy way to message us.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold">
                  Visit our page <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </a>
            </StaggerItem>
          </Stagger>

          <Reveal className="mt-12">
            <div className="rounded-3xl border border-sky-200 bg-sky-100/70 p-8 sm:p-10 dot-grid" data-testid="contact-service-area-section">
              <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                <div className="flex-1">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                    <MapPin className="h-5 w-5 text-sky-600" aria-hidden="true" />
                    Where we clean
                  </h2>
                  <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                    {BUSINESS.name} is a mobile cleaning service — we come to you. We serve
                    {" "}{AREAS.map((a) => `${a.city}, ${a.state}`).join(" · ")}{" "}
                    and surrounding communities across the Kansas City metro.
                  </p>
                </div>
                <Link
                  to="/quote"
                  data-testid="contact-quote-button"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-sky-600 px-7 py-4 text-base font-semibold text-white shadow-lg shadow-sky-600/25 transition-all duration-200 hover:bg-sky-700 active:scale-[0.98]"
                >
                  Get a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
